// PWA — 最近生成列表的 React Hook（Req 8.5 / 任务 9.7）
//
// 对 recentStore 的 IndexedDB 读写做一层 React 封装，组件可直接订阅最近列表，
// 并通过 add / clear 触发更新后自动刷新内存状态。保持自包含，不改动 App.tsx。

import { useCallback, useEffect, useState } from "react";
import type { RecentGeneration } from "@comfy/shared";
import {
  addRecent,
  clearRecent,
  getRecent,
  type RecentStoreOptions,
} from "./recentStore.js";

export interface UseRecentResult {
  /** 当前最近生成记录（按 createdAt 倒序，最多 MAX_RECENT 条）。 */
  recent: RecentGeneration[];
  /** 是否仍在首次加载。 */
  loading: boolean;
  /** 读取过程中遇到的错误（若有）。 */
  error: Error | null;
  /** 新增一条记录并刷新内存状态。 */
  add: (entry: RecentGeneration) => Promise<void>;
  /** 清空全部记录并刷新内存状态。 */
  clear: () => Promise<void>;
  /** 从 IndexedDB 重新加载。 */
  reload: () => Promise<void>;
}

export function useRecent(opts?: RecentStoreOptions): UseRecentResult {
  const [recent, setRecent] = useState<RecentGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    try {
      const list = await getRecent(opts);
      setRecent(list);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
    // opts 通常稳定（默认 undefined）；如需自定义 factory 请保持引用稳定。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const add = useCallback(
    async (entry: RecentGeneration) => {
      const next = await addRecent(entry, opts);
      setRecent(next);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [],
  );

  const clear = useCallback(async () => {
    await clearRecent(opts);
    setRecent([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { recent, loading, error, add, clear, reload };
}
